import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fromCanonicalMeasurement,
  getDefaultMeasurementSystem,
  toCanonicalMeasurement,
} from "../../utils/form/measurementUnits";

const unitSelectClass = "outlined-button !border-greyscale-3 !text-greyscale-3";

function MeasurementUnitInput({
  question,
  value,
  onChange,
  onEnter,
  inputRef,
  t,
}) {
  const { i18n } = useTranslation();
  const defaultSystem = getDefaultMeasurementSystem(
    i18n.resolvedLanguage || i18n.language,
  );
  const [unitSystem, setUnitSystem] = useState(defaultSystem);
  const [draft, setDraft] = useState(() =>
    fromCanonicalMeasurement(question.id, defaultSystem, value),
  );

  useEffect(() => {
    setUnitSystem(defaultSystem);
  }, [defaultSystem]);

  useEffect(() => {
    setDraft(fromCanonicalMeasurement(question.id, unitSystem, value));
  }, [question.id, unitSystem, value]);

  const emitCanonicalValue = (nextDraft) => {
    const canonical = toCanonicalMeasurement(
      question.id,
      unitSystem,
      nextDraft,
    );
    onChange({ target: { name: question.id, value: canonical } });
  };

  const updateDraft = (key, nextValue) => {
    const nextDraft = { ...draft, [key]: nextValue };
    setDraft(nextDraft);
    emitCanonicalValue(nextDraft);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onEnter?.(e);
    }
  };

  const renderNumberInput = (key, label, index = 0, showSuffix = true) => (
    <label className="flex-1 min-w-0">
      <span className="sr-only">{label}</span>
      <div className="flex items-center gap-2">
        <input
          ref={index === 0 ? inputRef : undefined}
          id={index === 0 ? question.id : `${question.id}-${key}`}
          name={`${question.id}-${key}`}
          type="number"
          inputMode="decimal"
          min="0"
          className="custom-input w-full appearance-none"
          placeholder={label}
          value={draft[key] ?? ""}
          onChange={(e) => updateDraft(key, e.target.value)}
          onKeyDown={handleKeyDown}
          required={false}
        />
        {showSuffix && (
          <span className="muted-text whitespace-nowrap">{label}</span>
        )}
      </div>
    </label>
  );

  const unitOptionLabel =
    question.id === "height"
      ? `${t("units.ft")}/${t("units.in")}`
      : question.id === "weight"
        ? `${t("units.st")}/${t("units.lb")}`
        : t("units.in");

  const unitSelect = (
    <label className="self-stretch flex items-stretch">
      <span className="sr-only">{t("units.select")}</span>
      <select
        className={unitSelectClass}
        value={unitSystem}
        onChange={(e) => setUnitSystem(e.target.value)}
      >
        <option value="metric">
          {question.id === "weight" ? t("units.kg") : t("units.cm")}
        </option>
        <option value="imperial">{unitOptionLabel}</option>
      </select>
    </label>
  );

  return (
    <div className="space-y-3">
      {unitSystem === "metric" && (
        <div className="flex items-stretch gap-2">
          {renderNumberInput(
            "value",
            question.id === "weight" ? t("units.kg") : t("units.cm"),
            0,
            false,
          )}
          {unitSelect}
        </div>
      )}

      {unitSystem === "imperial" && question.id === "height" && (
        <div className="flex items-stretch gap-2">
          <div className="grid grid-cols-2 gap-2 flex-1 min-w-0">
            {renderNumberInput("feet", t("units.ft"), 0)}
            {renderNumberInput("inches", t("units.in"), 1)}
          </div>
          {unitSelect}
        </div>
      )}

      {unitSystem === "imperial" && question.id === "chestCircumference" && (
        <div className="flex items-stretch gap-2">
          {renderNumberInput("inches", t("units.in"), 0, false)}
          {unitSelect}
        </div>
      )}

      {unitSystem === "imperial" && question.id === "weight" && (
        <div className="flex items-stretch gap-2">
          <div className="grid grid-cols-2 gap-2 flex-1 min-w-0">
            {renderNumberInput("stones", t("units.st"), 0)}
            {renderNumberInput("pounds", t("units.lb"), 1)}
          </div>
          {unitSelect}
        </div>
      )}
    </div>
  );
}

export default MeasurementUnitInput;
